import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../../../../../core/types/FormJsonTypes";
import { useCardAttributeCopyMutation } from "../../services/server/CardAttributeApi";
import type { CardAttributeCopyEntity } from "../../types/CardAttributeCopyEntity";
import cardAttributeCopyJson from "./card-attribute-copy.json";

export const CardAttributeCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = cardAttributeCopyJson as FormJson;

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id, cardImplementationId, cardDefinitionId } = useParams();

    const [formData, setFormData] = useState<CardAttributeCopyEntity>({} as CardAttributeCopyEntity);

    useEffect(() => {
        if (id) {
            setFormData((prev) => ({ ...prev, id }));
        }
    }, [id]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardAttributeCopyMutation();

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData || !cardImplementationId || !cardDefinitionId) return;

            const result = await saveFormData({
                data: formData,
                cardImplementationId,
                cardDefinitionId,
            });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: result.data.id,
                            resource: "CardAttribute",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(
                    `${BASE_PATH}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${cardDefinitionId}/${ResourceName.CARD_ATTRIBUTES}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [formData, saveFormData, dispatch, navigate, cardImplementationId, cardDefinitionId],
    );

    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardAttributeCopyEntity>
                    name="CardAttributeCopyForm"
                    formData={formData}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
